import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Edit, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const LegalPagesManagement = () => {
  const [pages, setPages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    page_type: '',
    title: '',
    content: '',
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await fetch('http://localhost/api/legal-pages.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Sayfalar yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setPages(data);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Sayfalar yüklenirken hata oluştu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingPage ? 'PUT' : 'POST';
      const url = 'http://localhost/api/legal-pages.php';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Sayfa kaydedilirken hata oluştu');
        return;
      }

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      toast.success(editingPage ? 'Sayfa güncellendi' : 'Sayfa eklendi');
      setIsModalOpen(false);
      setEditingPage(null);
      setFormData({
        page_type: '',
        title: '',
        content: '',
      });
      fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Sayfa kaydedilirken hata oluştu');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      page_type: page.page_type,
      title: page.title,
      content: page.content,
    });
    setIsModalOpen(true);
  };

  const openAddModal = (pageType, title) => {
    setEditingPage(null);
    setFormData({
      page_type: pageType,
      title: title,
      content: '',
    });
    setIsModalOpen(true);
  };

  const privacyPage = pages.find(p => p.page_type === 'privacy');
  const termsPage = pages.find(p => p.page_type === 'terms');

  return (
    <AdminLayout>
      <div>
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gizlilik Politikası */}
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Gizlilik Politikası</h3>
                {privacyPage ? (
                  <Button
                    onClick={() => handleEdit(privacyPage)}
                    variant="outline"
                    size="sm"
                  >
                    <Edit size={16} className="mr-1" />
                    Düzenle
                  </Button>
                ) : (
                  <Button
                    onClick={() => openAddModal('privacy', 'Gizlilik Politikası')}
                    variant="outline"
                    size="sm"
                  >
                    <Save size={16} className="mr-1" />
                    Oluştur
                  </Button>
                )}
              </div>
              {privacyPage ? (
                <div>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-3">
                    {privacyPage.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                  </p>
                  <p className="text-xs text-gray-500">
                    Son güncelleme: {new Date(privacyPage.updated_at).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Henüz içerik eklenmemiş</p>
              )}
            </div>

            {/* Kullanım Şartları */}
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Kullanım Şartları</h3>
                {termsPage ? (
                  <Button
                    onClick={() => handleEdit(termsPage)}
                    variant="outline"
                    size="sm"
                  >
                    <Edit size={16} className="mr-1" />
                    Düzenle
                  </Button>
                ) : (
                  <Button
                    onClick={() => openAddModal('terms', 'Kullanım Şartları')}
                    variant="outline"
                    size="sm"
                  >
                    <Save size={16} className="mr-1" />
                    Oluştur
                  </Button>
                )}
              </div>
              {termsPage ? (
                <div>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-3">
                    {termsPage.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                  </p>
                  <p className="text-xs text-gray-500">
                    Son güncelleme: {new Date(termsPage.updated_at).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Henüz içerik eklenmemiş</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPage ? 'Sayfayı Düzenle' : 'Yeni Sayfa Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık</label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Sayfa başlığı"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">İçerik (HTML desteklenir)</label>
              <Textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Sayfa içeriği"
                rows={20}
                required
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700"
              >
                <Save size={16} className="mr-2" />
                {editingPage ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default LegalPagesManagement;

